import {useEffect, useState} from "react";
import {Swiper, SwiperSlide} from "swiper/react";
import {Navigation} from "swiper";
import TourProperties from "@/components/tour-list/tour-list-v1/TourProperties.jsx";
import {getRecentlyViewedTrips} from "@/utils/recentlyViewedTrips.js";

// A lightweight "Ostatnio oglądane" strip for returning visitors. Reads the client-only
// localStorage snapshot recorded on tour-single mount (see recordRecentlyViewedTrip). There's
// nothing to show on a first visit, so the whole section - title included - is skipped rather
// than rendering an empty shell; reads happen in an effect (not a useState initializer) so this
// also can't ever be a source of SSR/CSR markup mismatch.
const RecentlyViewed = () => {
  const [trips, setTrips] = useState([]);

  useEffect(() => {
    setTrips(getRecentlyViewedTrips());
  }, []);

  if (trips.length === 0) {
    return null;
  }

  return (
    <section className="pt-50 pb-80 sm:pt-30 sm:pb-50">
      <div className="container">
        <div className="row justify-between items-end">
          <div className="col-auto">
            <div className="sectionTitle -md">
              <h2 className="sectionTitle__title">Ostatnio oglądane</h2>
              <p className="sectionTitle__text mt-5 sm:mt-0">
                Wróć do wycieczek, które już oglądałeś/aś
              </p>
            </div>
          </div>
        </div>
        {/* End .row */}

        <div className="relative mt-25 sm:mt-20 js-section-slider">
          <Swiper
            spaceBetween={20}
            className="overflow-visible"
            modules={[Navigation]}
            navigation={{
              nextEl: ".js-recently-viewed-next_active",
              prevEl: ".js-recently-viewed-prev_active",
            }}
            // slidesPerView stays at 1 across every breakpoint - deliberately, not an oversight.
            // Each slide renders TourProperties, the same list-row card used on /tour-list-v1
            // and SimilarPriceTrips: a fixed-width 300px image + flexible text + a fixed-width
            // price/CTA column, laid out with Bootstrap's col-md-auto/col-md, which switch to a
            // non-stacked row based on *viewport* width, not the width of whatever container
            // they're actually in. At slidesPerView 2 (or 1.3), each slide is only ~500-650px
            // wide at typical desktop viewports, well under what that row needs once its two
            // non-shrinking columns (image + price/CTA, ~500px between them) are accounted for -
            // the flexible middle column collapsed to near nothing and the itinerary paragraph
            // wrapped into dozens of lines, which is what made these cards render enormous.
            // Showing exactly one full-width slide keeps the row at (approximately) the same
            // width it already renders at everywhere else it's used, so it lays out correctly
            // instead of being squeezed.
          >
            {trips.map((trip) => (
              <SwiperSlide key={trip.id}>
                <div className="row">
                  <TourProperties toursData={[trip]} suppressNoHotelBanner={true} />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Start navigation buttons for next/prev slide */}
          <button
            className="section-slider-nav -prev flex-center bg-white text-dark-1 size-40 rounded-full shadow-1 sm:d-none js-recently-viewed-prev_active"
            aria-label="Poprzednio oglądane wycieczki"
          >
            <i className="icon icon-chevron-left text-12" />
          </button>
          <button
            className="section-slider-nav -next flex-center bg-white text-dark-1 size-40 rounded-full shadow-1 sm:d-none js-recently-viewed-next_active"
            aria-label="Kolejne oglądane wycieczki"
          >
            <i className="icon icon-chevron-right text-12" />
          </button>
          {/* End navigation buttons */}
        </div>
        {/* End slider */}
      </div>
      {/* End .container */}
    </section>
  );
};

export default RecentlyViewed;
